import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './utimacoCircuitSwitch.api';
import { getSelectedUtimacoCircuitSwitch } from './utimacoCircuitSwitch.selectors';
import {
  GET_UTIMACO_CIRCUIT_SWITCH_LIST,
  GET_UTIMACO_CIRCUIT_SWITCH,
  CREATE_UTIMACO_CIRCUIT_SWITCH,
  UPDATE_UTIMACO_CIRCUIT_SWITCH,
  DELETE_UTIMACO_CIRCUIT_SWITCH,
  GO_TO_UTIMACO_CIRCUIT_SWITCH_LIST,
  GO_TO_UTIMACO_CIRCUIT_SWITCH_EDIT,
  GO_TO_UTIMACO_CIRCUIT_SWITCH_VIEW,
  GO_TO_UTIMACO_CIRCUIT_SWITCH_CREATE,
  SELECT_UTIMACO_CIRCUIT_SWITCH,
  DeleteUtimacoCircuitSwitchThunkAction,
  UpdateUtimacoCircuitSwitchThunkAction,
  CreateUtimacoCircuitSwitchThunkAction,
  SET_UTIMACO_CIRCUIT_SWITCH_LOADING,
  SetUtimacoCircuitSwitchLoadingAction,
  UtimacoCircuitSwitchActionTypes,
  UtimacoCircuitSwitch,
  SelectUtimacoCircuitSwitchAction,
} from './utimacoCircuitSwitch.types';
import { UTIMACO_CIRCUIT_SWITCH_ROUTE, SYSTEM_CONFIGURATION_ROUTE, MISCELLANEOUS } from '../../constants';

export function goToUtimacoCircuitSwitchList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_CIRCUIT_SWITCH_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_CIRCUIT_SWITCH_ROUTE}`);
  };
}

export function goToUtimacoCircuitSwitchCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_CIRCUIT_SWITCH_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_CIRCUIT_SWITCH_ROUTE}/create`);
  };
}

export function goToUtimacoCircuitSwitchEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_CIRCUIT_SWITCH_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_CIRCUIT_SWITCH_ROUTE}/edit`);
  };
}

export function goToUtimacoCircuitSwitchView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_UTIMACO_CIRCUIT_SWITCH_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${MISCELLANEOUS}${UTIMACO_CIRCUIT_SWITCH_ROUTE}/view`);
  };
}

export function setUtimacoCircuitSwitchLoadingAction(
  isLoading: boolean,
): SetUtimacoCircuitSwitchLoadingAction {
  return {
    type: SET_UTIMACO_CIRCUIT_SWITCH_LOADING,
    payload: { isLoading },
  };
}

export function getUtimacoCircuitSwitchList(deliveryFunctionId: string): UtimacoCircuitSwitchActionTypes {
  return {
    type: GET_UTIMACO_CIRCUIT_SWITCH_LIST,
    payload: api.getUtimacoCircuitSwitchList(deliveryFunctionId),
  };
}

export function getUtimacoCircuitSwitch(
  deliveryFunctionId: string, id: string,
): UtimacoCircuitSwitchActionTypes {
  return {
    type: GET_UTIMACO_CIRCUIT_SWITCH,
    payload: api.getUtimacoCircuitSwitch(deliveryFunctionId, id),
  };
}

export function createUtimacoCircuitSwitch(
  deliveryFunctionId: string,
  body: WithAuditDetails<UtimacoCircuitSwitch>,
  history: History,
): CreateUtimacoCircuitSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_UTIMACO_CIRCUIT_SWITCH,
        payload: api.createUtimacoCircuitSwitch(deliveryFunctionId, body),
      });

      dispatch(getUtimacoCircuitSwitchList(deliveryFunctionId));
      dispatch(goToUtimacoCircuitSwitchList(history));
      showSuccessMessage(i18n.t('utimacoCircuitSwitch.create.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('utimacoCircuitSwitch.create.failed', { error: errorMessage }));
    }
  };
}

export function createUtimacoCircuitSwitchFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<UtimacoCircuitSwitch>,
): CreateUtimacoCircuitSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setUtimacoCircuitSwitchLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_UTIMACO_CIRCUIT_SWITCH,
        payload: api.createUtimacoCircuitSwitch(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('utimacoCircuitSwitch.create.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('utimacoCircuitSwitch.create.failed', { error: errorMessage }));

    } finally {
      dispatch(setUtimacoCircuitSwitchLoadingAction(false));
      dispatch(getUtimacoCircuitSwitchList(deliveryFunctionId));
    }
  };
}

export function updateUtimacoCircuitSwitch(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<UtimacoCircuitSwitch>,
  history: History,
): UpdateUtimacoCircuitSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_UTIMACO_CIRCUIT_SWITCH,
        payload: api.updateUtimacoCircuitSwitch(deliveryFunctionId, id, body),
      });

      dispatch(getUtimacoCircuitSwitchList(deliveryFunctionId));
      dispatch(goToUtimacoCircuitSwitchList(history));
      showSuccessMessage(i18n.t('utimacoCircuitSwitch.update.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('utimacoCircuitSwitch.update.failed', { error: errorMessage }));
    }
  };
}

export function deleteUtimacoCircuitSwitch(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteUtimacoCircuitSwitchThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedUtimacoCircuitSwitch] = getSelectedUtimacoCircuitSwitch(getState());

    try {
      await dispatch({
        type: DELETE_UTIMACO_CIRCUIT_SWITCH,
        payload: api.deleteUtimacoCircuitSwitch(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('utimacoCircuitSwitch.delete.success'), 5000);
      if (history != null) {
        dispatch(goToUtimacoCircuitSwitchList(history));
      }
    } catch (e) {
      showError(i18n.t('utimacoCircuitSwitch.delete.failed'));

    } finally {
      dispatch(getUtimacoCircuitSwitchList(deliveryFunctionId));
    }
  };
}

export function selectUtimacoCircuitSwitch(
  utimacoCircuitSwitch: UtimacoCircuitSwitch[],
): SelectUtimacoCircuitSwitchAction {
  return {
    type: SELECT_UTIMACO_CIRCUIT_SWITCH,
    payload: utimacoCircuitSwitch,
  };
}
